import { defineStore, acceptHMRUpdate } from 'pinia';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import moment from '@/utils/moment-wrapper.ts';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import {
    deleteLens,
    deleteLeoMemory,
    fetchConversation,
    fetchLenses,
    fetchLeoMemories,
    fetchUserConversations,
    fetchConversationFilePage as fetchConversationFilePageApi,
    getAskPendoPostRequest,
    getInitialState,
    cleanupIncompleteStoppedMessages,
    parseMessageContent,
    STOPPED_BY_NAVIGATION_MESSAGE,
    STOPPED_BY_USER_MESSAGE,
    postLens,
    postReportTrigger,
    putLens,
    putLeoMemory,
} from '@/stores/helpers/ask-pendo.helper';
import { ConversationSocket } from '@/stores/helpers/ask-pendo.conversation-socket';
import { FRAME } from '@/stores/helpers/ask-pendo.frames';
import { StreamingProcessor } from '@/stores/helpers/ask-pendo.streaming';
import { startPollingReport as runReportPoll } from '@/stores/helpers/ask-pendo.polling';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useAuthStore } from '@/stores/auth.store';

const ACTIVE_LENS_SETTING = 'leoActiveLensId';

// The Lens is session-scoped: it clears on refresh, on navigation, and on each new
// conversation. The persistence pipeline stays in place. Set this to true to restore
// the saved-Lens behavior.
const PERSIST_ACTIVE_LENS = false;

/**
 * A conversation carries page context only when user_context describes something the user
 * is looking at. Suggested questions alone are just starter prompts, so a conversation
 * opened from the top nav on an uncontextualized page still gets the active lens.
 */
/**
 * The trailing question nobody has answered yet, or null.
 *
 * Only the final message counts. An earlier user message already has its reply, and
 * sending it again would ask a question the conversation has already answered.
 */
export function trailingUnansweredQuestion (conversation) {
    const messages = conversation?.messages || [];
    const last = messages[messages.length - 1];

    return last?.type === 'user' ? last : null;
}

export function hasPageContext (userContext) {
    if (isEmpty(userContext)) return false;

    return Object.keys(userContext).some((key) => key !== 'suggestedQuestions');
}

// Keyed by conversation rather than by panel, because the panel remounts as the user
// moves and the socket has to outlive that.
const conversationSockets = new Map();

const cancelledSocketTurns = new Set();
const CONNECTION_FRAMES = new Set([FRAME.SOCKET_STATE, FRAME.CONVERSATION_REFRESHED, FRAME.MESSAGES_SNAPSHOT]);

/**
 * Fold one inbound frame into the store.
 *
 * Everything the agent streams keeps the names the SSE route gave it, so most of this
 * switch is the same vocabulary the SSE path already renders — one place for both
 * transports rather than two that drift.
 */
function applyFrameToStore (store, conversationId, frame) {
    switch (frame.type) {
        case 'assistant':
            if (frame.tool_calls?.length > 0) {
                store.addMessage({ conversationId, type: 'assistant', content: '', data: frame });
            }
            break;
        case 'tool':
            store.addMessage({ conversationId, type: 'tool', content: frame.content, data: frame });
            break;
        case 'workflow_state':
            store.setWorkflowState({ conversationId, workflowState: frame.workflow });
            break;
        case 'plan_state':
            store.setPlanState({ conversationId, planState: frame.plan });
            break;
        case 'todo_update':
            store.updateTodo({ conversationId, updatedTodo: frame.todo });
            break;
        case FRAME.MESSAGES_SNAPSHOT:
            store.mergeConversationFromFinalState({
                id: conversationId,
                finalState: { messages: [...frame.messages, ...frame.sentAfterInit] },
            });
            break;
        case 'done':
            if (frame.final_state) {
                store.mergeConversationFromFinalState({
                    id: conversationId,
                    finalState: frame.final_state,
                });

                // What starts the poll, and what both SSE paths do on their own
                // done. A report not reconciled here never enters pendingReports,
                // so nothing polls it and it never resolves.
                if (Array.isArray(frame.final_state.pending_reports)) {
                    store.reconcilePendingReports({
                        conversationId,
                        entries: frame.final_state.pending_reports,
                    });
                }
            }
            store.stopLoading(conversationId);
            break;
        case FRAME.CONVERSATION_REFRESHED:
            store.setConversation({ id: conversationId, conversation: frame.conversation });
            break;
        default:
            break;
    }
}

function frameHandlerFor (store, conversationId, onEvent) {
    return (frame) => {
        if (cancelledSocketTurns.has(conversationId) && !CONNECTION_FRAMES.has(frame.type)) {
            if (frame.type === 'done') {
                cancelledSocketTurns.delete(conversationId);
                applyFrameToStore(store, conversationId, frame);
            }

            return;
        }
        applyFrameToStore(store, conversationId, frame);
        if (typeof onEvent === 'function') onEvent(frame);
    };
}

/**
 * The supervisor locks a conversation's Lens on the first completed exchange and
 * ignores the client's Lens after that, so a locked chat turn sends none. Signals
 * has no server-side lock, so it must keep sending the locked scope.
 */
function lensForConversation (existingConversation, activeLens, type) {
    if (!existingConversation?.lensLocked) return activeLens;

    return type === 'signals' ? existingConversation.lockedLens : null;
}

function clientInitForSocket ({ employeeConfig, userRole, customInstructions, suggestedPrompt, preferredAppId }) {
    const fields = {};

    if (!isEmpty(employeeConfig)) fields.employeeConfig = employeeConfig;
    if (userRole) fields.userRole = userRole;
    if (customInstructions) fields.customInstructions = customInstructions;
    if (suggestedPrompt) fields.suggestedPrompt = true;
    if (preferredAppId) fields.preferredAppId = preferredAppId;

    return fields;
}

const pollCancellers = new Map();
function pollKey (conversationId, reportId) {
    return `${conversationId}::${reportId}`;
}

let loadLensesPromise = null;
let restoreLensPromise = null;
let lensMutationEpoch = 0;
let loadMemoriesPromise = null;
let memoryMutationEpoch = 0;

export const useAskPendoStore = defineStore('askPendo', {
    state: getInitialState,
    getters: {
        activeLens: (state) => state.lenses.find((lens) => lens.id === state.activeLensId) || null,
        awaitingFirstResponse: (state) => (id) => {
            const convo = state.conversations[id];
            const hasMessages = convo && convo.messages;
            const isFirstMessage = hasMessages && convo.messages.length === 1;

            return isFirstMessage && convo.messages[0].type === 'user' && convo.messages[0].metadata?.heading_type !== 'ask_question';
        },
        conversationById: (state) => (id) => state.conversations[id] || null,
        conversationHasMessages: (state) => (id) => {
            const convo = state.conversations[id];

            return convo && Array.isArray(convo.messages) && convo.messages.length > 0;
        },
        getArtifactConfig: (state) => (artifactId) => state.artifactConfig[artifactId] || null,
        getArtifactMenuItems: (state) => (artifactId) => state.artifactMenuItems[artifactId] || [],
        getConversationContext: (state) => (route) => {
            const conversationId = route?.name === 'LeoConversation'
                ? route.params.id
                : route?.query?.leo;

            if (!conversationId) return null;

            return state.conversations[conversationId]?.user_context;
        },
        getConversationFile: (state) => (conversationId, filePath) => {
            if (!conversationId || !filePath) return null;

            return state.conversations[conversationId]?.files?.[filePath] ?? null;
        },
        isResponsePending: (state) => (id) => state.pendingResponses[id] || false,
        pendingReportsForConversation: (state) => (id) => {
            const bucket = state.pendingReports?.[id];
            if (!bucket) return [];

            return Object.values(bucket);
        },
        hasPendingReports: (state) => (id) => {
            const bucket = state.pendingReports?.[id];

            return !!bucket && Object.keys(bucket).length > 0;
        },
    },
    actions: {
        addMessage ({ conversationId, type, content, metadata = {}, data = {}, streamId = null }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const message = {
                type,
                content,
                timestamp: moment().toISOString(),
                metadata,
                ...data,
            };

            if (streamId) {
                message.streamId = streamId;
            }

            conversation.messages.push(message);
            cleanupIncompleteStoppedMessages(conversation.messages);
        },
        addStateToConversation ({ conversationId, fieldName, fieldData }) {
            const conversation = this.conversations[conversationId];
            conversation.state_details[fieldName] = fieldData;
        },
        clearActiveStreamId ({ conversationId }) {
            if (this.activeStreamIds) {
                delete this.activeStreamIds[conversationId];
            }
        },
        clearAbortController ({ conversationId }) {
            delete this.abortControllers[conversationId];
        },
        clearArtifactMenuItems (artifactId) {
            delete this.artifactMenuItems[artifactId];
        },
        removeLastExchange ({ conversationId }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const { messages, artifacts } = conversation;

            let lastUserIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i]?.type === 'user') {
                    lastUserIndex = i;
                    break;
                }
            }
            if (lastUserIndex === -1) return;

            const removedArtifactIds = new Set();
            for (let i = lastUserIndex; i < messages.length; i++) {
                const ids = messages[i]?.metadata?.artifact_ids;
                if (Array.isArray(ids)) {
                    for (const id of ids) removedArtifactIds.add(id);
                }
            }

            messages.length = lastUserIndex;

            if (artifacts && removedArtifactIds.size) {
                for (const id of removedArtifactIds) {
                    delete artifacts[id];
                }
            }

            if (conversation.last_artifact_id && removedArtifactIds.has(conversation.last_artifact_id)) {
                delete conversation.last_artifact_id;
            }
        },
        removeIncompleteMessages ({ conversationId, streamId = null, stoppedByNavigation = false }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const { messages } = conversation;
            const isBlank = (item) => !item || item.trim() === '';
            const stopNotice = stoppedByNavigation ? STOPPED_BY_NAVIGATION_MESSAGE : STOPPED_BY_USER_MESSAGE;

            const isIncompleteAssistant = (msg, targetStreamId) =>
                msg.type === 'assistant' &&
                msg.tool_calls?.length > 0 &&
                isBlank(parseMessageContent(msg).content) &&
                (!targetStreamId || msg.streamId === targetStreamId);

            const cleanAssistant = (msg) => {
                const cleaned = { ...msg };
                delete cleaned.tool_calls;
                if (isBlank(parseMessageContent(cleaned).content)) cleaned.content = stopNotice;

                return cleaned;
            };

            const toolCallIds = new Set();

            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (isIncompleteAssistant(msg, streamId)) {
                    for (const tc of msg.tool_calls) toolCallIds.add(tc.id);
                    messages[i] = cleanAssistant(msg);
                    break;
                }
            }

            if (toolCallIds.size > 0) {
                remove(messages, (msg) => msg && msg.type === 'tool' && toolCallIds.has(msg.tool_call_id));
            }
            cleanupIncompleteStoppedMessages(messages);
        },
        setAbortController ({ conversationId, controller }) {
            this.abortControllers[conversationId] = controller;
        },
        setActiveLens (lensId, { persist = true } = {}) {
            this.activeLensId = lensId;
            if (persist) {
                this.lensRestoreState = 'done';
                if (PERSIST_ACTIVE_LENS) {
                    useUserSettingsStore()
                        .update({ namespaced: true, name: ACTIVE_LENS_SETTING, value: lensId })
                        .catch(() => {});
                }
            }
        },
        setActiveStreamId ({ conversationId, streamId }) {
            this.activeStreamIds[conversationId] = streamId;
        },
        setArtifactConfig ({ artifactId, config }) {
            this.artifactConfig[artifactId] = assign({}, this.artifactConfig[artifactId], config);
        },
        setArtifactMenuItems ({ artifactId, items }) {
            this.artifactMenuItems[artifactId] = items;
        },
        setConfigDrawerState ({ isOpen }) {
            this.configDrawerOpen = isOpen;
        },
        setMentionShowOutOfLensScope ({ show }) {
            this.mentionShowOutOfLensScope = show;
        },
        setMentionScopeLensName ({ name }) {
            this.mentionScopeLensName = name;
        },
        /**
         * The fetched copy, unless what we already hold is further along.
         *
         * The server's copy is written per turn by the proxy, so it can be behind what
         * this tab has streamed. Replacing a longer history with a shorter one deletes
         * turns from the user's screen and from the next socket's payload.
         *
         * Message count is a crude test and a safe one: history only grows within a
         * conversation, so fewer messages means staler, never different.
         */
        freshestConversation (id, fetched) {
            const held = this.conversations[id];
            const heldCount = held?.messages?.length || 0;
            const fetchedCount = fetched?.messages?.length || 0;

            if (heldCount <= fetchedCount) return fetched;

            // Everything the fetch knows except its shorter history — it may carry
            // artifacts or pending reports this tab never saw.
            return { ...fetched, messages: held.messages };
        },
        setConversation ({ id, conversation }) {
            this.conversations[id] = conversation;
        },
        /**
         * Fold a turn's final state into the conversation we already hold.
         *
         * The socket carries many turns, and replacing the whole conversation object on
         * each `done` drops anything the new state does not mention — the lens lock the
         * FE sets itself, or an artifact from an earlier turn. The SSE path names the
         * lens keys and copies them across, which holds only until someone adds a key;
         * merging holds generally.
         *
         * Messages still come from the turn's state. Owning them locally is the larger
         * change and it needs ai-connect to emit the turn's delta first; without that the
         * FE has no durable record of the tool calls and reasoning it only ever held
         * transiently.
         */
        mergeConversationFromFinalState ({ id, finalState }) {
            const previous = this.conversations[id] || {};

            this.conversations[id] = {
                ...previous,
                ...finalState,
                conversationId: id,
                // The server echoes back the context it was given, so a turn that
                // finishes after the user dismissed it would put it back. Same rule
                // the request path applies to its own responses: a dismissal outlives
                // anything the server says.
                ...(previous.contextDismissed && { user_context: null, contextDismissed: true }),
            };
        },
        setConversationUserContext ({ id, userContext }) {
            const conversation = this.conversations[id];
            // Once the user has cleared the context, nothing may put one back.
            if (!conversation || conversation.contextDismissed) return;

            conversation.user_context = userContext;
        },
        // A dismissal outlives navigation and a server response. Nothing restores the context.
        dismissConversationUserContext ({ id }) {
            if (this.conversations[id]) {
                this.conversations[id].user_context = null;
                this.conversations[id].contextDismissed = true;
            }
        },
        setConversationLockedLens ({ conversationId, lockedLens }) {
            if (this.conversations[conversationId]) {
                this.conversations[conversationId].lensLocked = true;
                this.conversations[conversationId].lockedLens = lockedLens;
            }
        },
        setUserConversations ({ conversations }) {
            this.userConversations = conversations;
        },
        setRecentConversations ({ conversations }) {
            this.recentConversations = conversations;
        },
        setEmployeeConfig ({ employeeConfig }) {
            this.employeeConfig = employeeConfig;
        },
        setCustomInstructions ({ customInstructions }) {
            this.customInstructions = customInstructions;
        },
        setPreferredAppId ({ preferredAppId }) {
            this.preferredAppId = preferredAppId || 0;
        },
        setResponsePending ({ id, isPending }) {
            this.pendingResponses[id] = isPending;
        },
        setWorkflowState ({ conversationId, workflowState }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            conversation.workflow_state = workflowState;
        },
        setPlanState ({ conversationId, planState }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            conversation.plan = planState;
        },
        updateTodo ({ conversationId, updatedTodo }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const todos = conversation.plan.todos.map((t) => (t.id === updatedTodo.id ? updatedTodo : t));
            conversation.plan = { ...conversation.plan, todos };
        },
        addPendingReport ({ conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            if (!this.pendingReports[conversationId]) {
                this.pendingReports[conversationId] = {};
            }
            if (!this.pendingReports[conversationId][entry.report_id]) {
                this.pendingReports[conversationId][entry.report_id] = { ...entry };
            }
        },
        removePendingReport ({ conversationId, reportId }) {
            const bucket = this.pendingReports[conversationId];
            if (!bucket) return;
            delete bucket[reportId];
            if (Object.keys(bucket).length === 0) {
                delete this.pendingReports[conversationId];
            }
        },
        setPendingReports ({ conversationId, entries }) {
            if (!conversationId) return;
            const bucket = {};
            for (const entry of (entries || [])) {
                if (entry?.report_id) bucket[entry.report_id] = { ...entry };
            }
            if (Object.keys(bucket).length === 0) {
                delete this.pendingReports[conversationId];
            } else {
                this.pendingReports[conversationId] = bucket;
            }
        },
        updateArtifact ({ conversationId, artifactId, updates }) {
            const conversation = this.conversations[conversationId];
            if (!conversation?.artifacts?.[artifactId]) return;

            const artifact = conversation.artifacts[artifactId];
            artifact.data = { ...artifact.data, ...updates };
        },
        applyLiveArtifactRefresh ({ conversationId, artifactId, data, live }) {
            const conversation = this.conversations[conversationId];
            const artifact = conversation?.artifacts?.[artifactId];
            if (!artifact) return;

            if (data) artifact.data = data;
            if (live) artifact.live = live;
        },
        async createConversation ({
            id,
            prompt = null,
            type = 'user',
            metadata = {},
            artifacts = {},
            // eslint-disable-next-line camelcase
            user_context = {},
            files = {},
            suggestedPrompt = false,
            // Only a conversation opened to describe wherever the user is may follow them.
            contextFollowsPage = false,
        }) {
            const conversation = {
                conversationId: id,
                messages: [],
                artifacts,
                // eslint-disable-next-line camelcase
                user_context,
                files,
                suggestedPrompt,
                contextFollowsPage,
            };

            this.setConversation({ id, conversation });

            const hasPrompt = typeof prompt === 'string' && prompt.trim().length > 0;
            if (hasPrompt) {
                this.addMessage({
                    conversationId: id,
                    type,
                    content: prompt,
                    metadata,
                });
            }

            return conversation;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (this.userConversations.length > 0 && !reFetch) {
                return this.userConversations;
            }

            const { data } = await fetchUserConversations();
            this.setUserConversations({ conversations: data });

            return data;
        },
        async loadRecent ({ reFetch = false, limit = 3 } = {}) {
            if (this.recentConversations.length > 0 && !reFetch) {
                return this.recentConversations;
            }

            const { data } = await fetchUserConversations({ limit });
            this.setRecentConversations({ conversations: data });

            return data;
        },
        async loadLenses ({ reFetch = false } = {}) {
            if (!hasSegmentFlag('leoLens')) {
                return this.lenses;
            }

            if (loadLensesPromise) {
                return loadLensesPromise.catch((error) => {
                    if (this.lensesLoaded) {
                        return this.lenses;
                    }

                    throw error;
                });
            }

            if (this.lensesLoaded && !reFetch) {
                return this.lenses;
            }

            let promise = null;
            promise = (async () => {
                try {
                    const epochAtFetch = lensMutationEpoch;
                    const { data } = await fetchLenses();
                    if (lensMutationEpoch === epochAtFetch) {
                        this.lensesLoaded = true;
                        this.lenses = data || [];
                    }

                    return this.lenses;
                } finally {
                    if (loadLensesPromise === promise) {
                        loadLensesPromise = null;
                    }
                }
            })();
            loadLensesPromise = promise;

            return promise;
        },
        /**
         * Settle which Lens is active before anything reads it.
         *
         * Two things have to happen first and neither is obvious from
         * lensForConversation alone: a persisted Lens has to be restored from user
         * settings, and a conversation carrying page context has to give up the active
         * Lens, because the page owns the scoping.
         *
         * Shared because the request path and the socket both need it. The socket
         * needs it more: its scope is fixed at conversation_init and there is no frame
         * for changing it afterwards, so opening before this resolves means opening on
         * the wrong Lens for the life of the connection.
         *
         * Cheap after the first call — restoreActiveLens returns immediately once done,
         * and returns the in-flight promise rather than refetching.
         */
        async settleActiveLens (conversation) {
            if (!['pending', 'failed'].includes(this.lensRestoreState)) return;

            await this.restoreActiveLens().catch(() => {});

            if (hasPageContext(conversation?.user_context) && this.activeLensId) {
                this.setActiveLens(null, { persist: false });
            }
        },
        async restoreActiveLens () {
            if (!hasSegmentFlag('leoLens')) {
                this.lensRestoreState = 'done';

                return;
            }

            if (this.lensRestoreState === 'done') {
                return;
            }

            if (restoreLensPromise) {
                return restoreLensPromise;
            }

            const promise = (async () => {
                try {
                    if (PERSIST_ACTIVE_LENS) {
                        const userSettings = useUserSettingsStore();
                        await userSettings.loadAll({ reFetch: this.lensRestoreState === 'failed' });
                        if (isEmpty(userSettings.map)) {
                            throw userSettings.error || new Error('user settings unavailable');
                        }
                    }
                    await this.loadLenses();
                    if (this.lensRestoreState !== 'done') {
                        this.applyPersistedLens();
                        this.lensRestoreState = 'done';
                    }
                } catch (error) {
                    if (this.lensRestoreState !== 'done') {
                        this.lensRestoreState = 'failed';
                    }
                    throw error;
                } finally {
                    if (restoreLensPromise === promise) {
                        restoreLensPromise = null;
                    }
                }
            })();
            restoreLensPromise = promise;

            return restoreLensPromise;
        },
        applyPersistedLens () {
            if (!PERSIST_ACTIVE_LENS) return;

            const savedLensId = useUserSettingsStore().settingByName(ACTIVE_LENS_SETTING, true)?.value;
            if (savedLensId && this.lenses.some((lens) => String(lens.id) === String(savedLensId))) {
                this.activeLensId = String(savedLensId);
            }
        },
        async addLens (lens) {
            const { data } = await postLens(lens);
            lensMutationEpoch += 1;
            this.lenses.push(data);

            return data;
        },
        async updateLens (lens) {
            const { data } = await putLens(lens);
            lensMutationEpoch += 1;
            this.lenses = this.lenses.map((existingLens) => (existingLens.id === data.id ? data : existingLens));

            return data;
        },
        async removeLens (id) {
            await deleteLens(id);
            lensMutationEpoch += 1;
            this.lenses = this.lenses.filter((lens) => lens.id !== id);
            if (this.activeLensId === id) {
                this.setActiveLens(null);
            }
        },
        async loadMemories ({ reFetch = false } = {}) {
            if (!hasSegmentFlag('leoLongTermMemory')) {
                return this.memories;
            }

            if (loadMemoriesPromise) {
                return loadMemoriesPromise.catch((error) => {
                    if (this.memoriesLoaded) {
                        return this.memories;
                    }

                    throw error;
                });
            }

            if (this.memoriesLoaded && !reFetch) {
                return this.memories;
            }

            let promise = null;
            promise = (async () => {
                try {
                    const epochAtFetch = memoryMutationEpoch;
                    const { data } = await fetchLeoMemories();
                    if (memoryMutationEpoch === epochAtFetch) {
                        this.memoriesLoaded = true;
                        this.memories = data?.memories || [];
                    }

                    return this.memories;
                } finally {
                    if (loadMemoriesPromise === promise) {
                        loadMemoriesPromise = null;
                    }
                }
            })();
            loadMemoriesPromise = promise;

            return promise;
        },
        async updateMemory ({ id, content }) {
            const { data } = await putLeoMemory(id, content);
            memoryMutationEpoch += 1;
            const saved = { id, content, ...data?.memory };
            this.memories = this.memories.map((memory) => (memory.id === id ? { ...memory, ...saved } : memory));

            return saved;
        },
        async removeMemory (id) {
            await deleteLeoMemory(id);
            memoryMutationEpoch += 1;
            this.memories = this.memories.filter((memory) => memory.id !== id);
        },
        async loadOne ({ id, reFetch = false }) {
            if (!reFetch && this.conversationById(id)) {
                return Promise.resolve();
            }

            try {
                const { data } = await fetchConversation(id);
                this.setConversation({
                    id: data.conversationId,
                    conversation: this.freshestConversation(data.conversationId, data),
                });

                if (Array.isArray(data.pending_reports) && data.pending_reports.length > 0) {
                    this.hydratePendingReports({
                        conversationId: data.conversationId,
                        entries: data.pending_reports,
                    });
                }

                return data;
            } catch (error) {
                if (error.response?.status === 403 && error.response?.data?.reason === 'app_entitlement') {
                    error.status = 403;
                    error.reason = 'app_entitlement';
                    error.restrictedApps = error.response.data.restrictedApps;
                }
                throw error;
            }
        },
        async sendConversation ({ id, type, revision, force, suggestedPrompt = false, onStreamRead = () => {}, onStreamEnd = () => {} }) {
            this.startLoading(id);
            const conversation = this.conversations[id];

            if (!conversation) {
                this.stopLoading(id);
                throw new Error('Conversation not found');
            }

            const postRequest = getAskPendoPostRequest(type);
            if (!postRequest) {
                this.stopLoading(id);
                throw new Error('Invalid conversation type');
            }

            const existingController = this.abortControllers[conversation.conversationId];
            if (existingController) {
                existingController.abort();
                this.clearAbortController({ conversationId: conversation.conversationId });
            }

            const abortController = new AbortController();
            this.setAbortController({ conversationId: conversation.conversationId, controller: abortController });

            if (type === 'chat') {
                await this.settleActiveLens(conversation);
            }

            const existingConversation = this.conversationById(conversation.conversationId);
            const lensToSend = lensForConversation(existingConversation, this.activeLens, type);

            const options = { revision, force, signal: abortController.signal };

            if (type === 'signals' && hasSegmentFlag('signalsPerUserCache')) {
                options.perUserCache = true;
            }

            try {
                const data = await postRequest(
                    conversation,
                    this.employeeConfig,
                    options,
                    useAuthStore().user?.jobRole || '',
                    this.customInstructions || '',
                    suggestedPrompt,
                    this.preferredAppId || 0,
                    // Page context owns scoping — a context conversation never sends a lens.
                    hasPageContext(conversation.user_context) ? null : lensToSend,
                );

                if (data instanceof ReadableStream) {
                    let stringBuffer = '';

                    const streamStartTime = Date.now();
                    const streamId = `${conversation.conversationId}_${streamStartTime}`;

                    this.setActiveStreamId({ conversationId: conversation.conversationId, streamId });

                    const streamingProcessor = new StreamingProcessor({
                        onData: (chunk) => {
                            const currentActiveStreamId = this.activeStreamIds[conversation.conversationId];
                            if (currentActiveStreamId !== streamId) {
                                return;
                            }
                            switch (chunk.type) {
                                case 'assistant':
                                    if (chunk.tool_calls?.length > 0) {
                                        this.addMessage({
                                            conversationId: conversation.conversationId,
                                            type: 'assistant',
                                            content: '',
                                            data: chunk,
                                            streamId,
                                        });
                                    }
                                    stringBuffer += chunk.content;
                                    break;
                                case 'tool':
                                    this.addMessage({
                                        conversationId: conversation.conversationId,
                                        type: 'tool',
                                        content: chunk.content,
                                        data: chunk,
                                        streamId,
                                    });
                                    break;
                                case 'workflow_state':
                                    this.setWorkflowState({
                                        conversationId: conversation.conversationId,
                                        workflowState: chunk.workflow,
                                    });
                                    break;
                                case 'plan_state':
                                    this.setPlanState({
                                        conversationId: conversation.conversationId,
                                        planState: chunk.plan,
                                    });
                                    break;
                                case 'todo_update':
                                    this.updateTodo({
                                        conversationId: conversation.conversationId,
                                        updatedTodo: chunk.todo,
                                    });
                                    break;
                                case 'pending_report':
                                    break;
                            }
                            onStreamRead({ ...chunk, streamId });
                        },
                        onEnd: (finalState) => {
                            const activeStreamId = this.activeStreamIds[conversation.conversationId];
                            const isActiveStream = activeStreamId === streamId;

                            if (finalState?.lensLocked && isActiveStream) {
                                this.setConversationLockedLens({
                                    conversationId: conversation.conversationId,
                                    lockedLens: finalState.lockedLens ?? null,
                                });
                            }

                            if (!finalState || !stringBuffer || !isActiveStream) {
                                onStreamEnd();

                                return;
                            }

                            this.$patch((state) => {
                                const previous = state.conversations[conversation.conversationId];
                                state.conversations[conversation.conversationId] = {
                                    conversationId: conversation.conversationId,
                                    lensLocked: previous?.lensLocked,
                                    lockedLens: previous?.lockedLens,
                                    contextFollowsPage: previous?.contextFollowsPage,
                                    ...finalState,
                                    ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                                };
                                delete state.activeStreamIds[conversation.conversationId];
                            });

                            if (Array.isArray(finalState.pending_reports)) {
                                this.reconcilePendingReports({
                                    conversationId: conversation.conversationId,
                                    entries: finalState.pending_reports,
                                });
                            }

                            onStreamEnd();
                        },
                        onError: (error) => {
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                if (data.body.error) {
                    this.stopLoading(id);
                    throw new Error(data.body.error);
                }

                const previous = this.conversations[id];
                const conversationResponse = {
                    id,
                    lensLocked: previous?.lensLocked,
                    lockedLens: previous?.lockedLens,
                    contextFollowsPage: previous?.contextFollowsPage,
                    ...data.body,
                    ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                    messages: data.body.messages.map((msg) => ({
                        ...msg,
                        timestamp: moment().toISOString(),
                    })),
                };
                this.setConversation({ id, conversation: conversationResponse });
            } catch (error) {
                if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message.includes('aborted')) {
                    return;
                }
                const streamError = new Error(error.message);
                streamError.type = error.type;
                streamError.code = error.code;
                streamError.details = error.details;

                throw streamError;
            } finally {
                if (this.abortControllers[conversation.conversationId] === abortController) {
                    this.stopLoading(id);
                    this.clearAbortController({ conversationId: conversation.conversationId });
                }
            }
        },
        async fetchConversationFilePage ({ conversationId, fileId, pageToken, pageSize } = {}) {
            return fetchConversationFilePageApi(conversationId, fileId, { pageToken, pageSize });
        },
        setEmployeeConfigProperty ({ key, value }) {
            const employeeConfig = {
                ...this.employeeConfig,
                [key]: value,
            };
            this.setEmployeeConfig({ employeeConfig });
        },
        unsetEmployeeConfigProperty (key) {
            const { [key]: _removed, ...employeeConfig } = this.employeeConfig;
            this.setEmployeeConfig({ employeeConfig });
        },
        updateCustomInstructions (customInstructions) {
            this.setCustomInstructions({ customInstructions: customInstructions || '' });
        },
        updatePreferredAppId (preferredAppId) {
            this.setPreferredAppId({ preferredAppId: preferredAppId || 0 });
        },
        startLoading (conversationId) {
            this.setResponsePending({ id: conversationId, isPending: true });
        },
        stopLoading (conversationId) {
            this.setResponsePending({ id: conversationId, isPending: false });
        },
        conversationSocketIsLive (id) {
            return conversationSockets.get(id)?.isLive === true;
        },
        conversationSocketHasTurnInFlight (id) {
            return conversationSockets.get(id)?.turnInFlight === true;
        },
        // Outlives its panel only until `done`, which is what persists the turn. Closed here
        // rather than in the shared handler's callback, which a cancelled turn's `done` skips.
        releaseConversationSocket ({ id }) {
            const socket = conversationSockets.get(id);
            if (!socket?.isLive) return;

            const applyFrame = frameHandlerFor(this, id);
            socket.adopt({
                onEvent: (frame) => {
                    applyFrame(frame);
                    if (frame.type === 'done') this.closeConversationSocket({ id });
                },
            });
        },
        /**
         * Put a conversation on a socket, and ask anything outstanding.
         *
         * Adopts the live socket if there is one, because a remount is a change of
         * owner rather than of conversation. Otherwise opens a new one.
         *
         * Opening also starts the turn — see startTurnOnSocket. That is not separable:
         * the trailing question has to be withheld from the history the socket opens
         * with, so the decision has to be made before it opens, not by the caller
         * afterwards.
         */
        openConversationSocket ({ id, url, onEvent, SocketCtor = ConversationSocket } = {}) {
            const conversation = this.conversations[id];
            if (!conversation) {
                throw new Error(`openConversationSocket: no conversation in store for id ${id}`);
            }

            const handleFrame = frameHandlerFor(this, id, onEvent);

            // A remount is a change of owner, not of conversation. Closing the live
            // socket here would abandon a turn in flight, and the user would watch the
            // answer they were already reading stop halfway.
            const existing = conversationSockets.get(id);
            if (existing?.isLive) {
                existing.adopt({ onEvent: handleFrame });

                return existing;
            }

            if (existing) {
                existing.close();
                conversationSockets.delete(id);
            }

            const socket = new SocketCtor({
                conversationId: id,
                url,
                onEvent: handleFrame,
                // Same choice the request path makes in sendConversation: page context
                // owns scoping, so a context conversation never sends a Lens; otherwise
                // the active Lens, or none once the conversation is locked, because the
                // server owns the lock and applies it itself.
                lens: () => {
                    const current = this.conversationById(id);

                    return hasPageContext(current?.user_context)
                        ? null
                        : lensForConversation(current, this.activeLens, 'chat');
                },
                clientInit: () => {
                    const current = this.conversationById(id);
                    const fields = clientInitForSocket({
                        employeeConfig: this.employeeConfig,
                        userRole: useAuthStore().user?.jobRole,
                        customInstructions: this.customInstructions,
                        suggestedPrompt: current?.suggestedPrompt,
                        preferredAppId: this.preferredAppId,
                    });
                    // Only the first turn came from a suggested prompt; a reopen must not re-send it.
                    if (current) current.suggestedPrompt = false;

                    return fields;
                },
            });

            conversationSockets.set(id, socket);
            socket.open(this.startTurnOnSocket({ id, socket, conversation }));

            return socket;
        },
        /**
         * Ask the trailing question, and return the history to open with.
         *
         * The FE pushes the user's message optimistically before the socket exists, so
         * it is sent as its own frame and withheld from the init history — left in,
         * the server would replay it and answer it twice.
         *
         * Returns the conversation unchanged when there is nothing outstanding.
         */
        startTurnOnSocket ({ id, socket, conversation }) {
            const question = trailingUnansweredQuestion(conversation);
            if (!question) return conversation;

            socket.sendUserMessage(question);
            // Sending is what starts the turn on this path, and nothing else will show
            // the panel as busy — the component no longer kicks off a request of its
            // own, precisely so the question is not asked twice.
            this.startLoading(id);

            return { ...conversation, messages: conversation.messages.slice(0, -1) };
        },
        closeConversationSocket ({ id }) {
            const socket = conversationSockets.get(id);
            if (!socket) return;
            socket.close();
            conversationSockets.delete(id);
            cancelledSocketTurns.delete(id);
        },
        cancelSocketTurn ({ id, stoppedByNavigation = false }) {
            if (conversationSockets.get(id)?.cancelTurn()) {
                cancelledSocketTurns.add(id);
            }
            this.removeIncompleteMessages({ conversationId: id, stoppedByNavigation });
        },
        sendUserMessageViaSocket ({ id, message }) {
            const socket = conversationSockets.get(id);
            if (!socket) {
                throw new Error(`sendUserMessageViaSocket: no open socket for id ${id}`);
            }
            socket.sendUserMessage(message);
        },
        stopResponse ({ conversationId, stoppedByNavigation = false }) {
            const activeStreamId = this.activeStreamIds[conversationId];
            const controller = this.abortControllers[conversationId];
            if (controller) {
                controller.abort();
                this.clearAbortController({ conversationId });
            }
            this.clearActiveStreamId({ conversationId });
            if (activeStreamId) {
                this.removeIncompleteMessages({ conversationId, streamId: activeStreamId, stoppedByNavigation });
            }
            this.stopLoading(conversationId);
        },
        updateConfigDrawerState ({ isOpen }) {
            this.setConfigDrawerState({ isOpen });
        },
        trackPendingReport ({ conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            const existing = this.pendingReports[conversationId]?.[entry.report_id];
            this.addPendingReport({ conversationId, entry });
            if (!existing) {
                this.startPollingReport({ conversationId, reportId: entry.report_id });
            }
        },
        reconcilePendingReports ({ conversationId, entries }) {
            if (!conversationId) return;
            const wanted = new Set();
            for (const entry of (entries || [])) {
                if (entry?.report_id) {
                    wanted.add(entry.report_id);
                    const existing = this.pendingReports[conversationId]?.[entry.report_id];
                    this.addPendingReport({ conversationId, entry });
                    if (!existing) {
                        this.startPollingReport({ conversationId, reportId: entry.report_id });
                    }
                }
            }

            const current = this.pendingReports[conversationId] || {};
            for (const reportId of Object.keys(current)) {
                if (!wanted.has(reportId)) {
                    this.stopPollingReport({ conversationId, reportId });
                    this.removePendingReport({ conversationId, reportId });
                }
            }
        },
        hydratePendingReports ({ conversationId, entries }) {
            if (!conversationId || !Array.isArray(entries)) return;
            for (const entry of entries) {
                if (entry?.report_id) {
                    this.trackPendingReport({ conversationId, entry });
                }
            }
        },
        startPollingReport ({ conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            const previous = pollCancellers.get(key);
            if (previous) {
                previous();
                pollCancellers.delete(key);
            }

            const cancel = runReportPoll({
                reportId,
                onDone: ({ status, missing }) => {
                    pollCancellers.delete(key);
                    if (!this.pendingReports[conversationId]?.[reportId]) return;
                    const triggerContent = status === 'success' ? 'report_done' : 'report_failed';
                    this.triggerReportSynthesis({
                        conversationId,
                        reportId,
                        triggerContent,
                        missing: missing === true,
                    });
                },
                onError: () => {},
            });

            pollCancellers.set(key, cancel);
        },
        stopPollingReport ({ conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            const cancel = pollCancellers.get(key);
            if (cancel) {
                cancel();
                pollCancellers.delete(key);
            }
        },
        async triggerReportSynthesis ({ conversationId, reportId, triggerContent }) {
            if (!conversationId || !reportId || !triggerContent) return;

            if (this.abortControllers[conversationId]) {
                this.startPollingReport({ conversationId, reportId });

                return;
            }

            const previousEntry = this.pendingReports[conversationId]?.[reportId];
            this.removePendingReport({ conversationId, reportId });

            this.startLoading(conversationId);

            const abortController = new AbortController();
            this.setAbortController({ conversationId, controller: abortController });

            try {
                const data = await postReportTrigger(
                    conversationId,
                    reportId,
                    triggerContent,
                    { signal: abortController.signal },
                    this.employeeConfig,
                    useAuthStore().user?.jobRole || '',
                    this.customInstructions || '',
                );

                if (!(data instanceof ReadableStream)) {
                    if (previousEntry) {
                        this.addPendingReport({ conversationId, entry: previousEntry });
                        this.startPollingReport({ conversationId, reportId });
                    }

                    return;
                }

                const streamStartTime = Date.now();
                const streamId = `${conversationId}_trigger_${reportId}_${streamStartTime}`;
                this.setActiveStreamId({ conversationId, streamId });

                const streamingProcessor = new StreamingProcessor({
                    onData: (chunk) => {
                        const currentActiveStreamId = this.activeStreamIds[conversationId];
                        if (currentActiveStreamId !== streamId) return;

                        switch (chunk.type) {
                            case 'assistant':
                                if (chunk.tool_calls?.length > 0) {
                                    this.addMessage({
                                        conversationId,
                                        type: 'assistant',
                                        content: '',
                                        data: chunk,
                                        streamId,
                                    });
                                }
                                break;
                            case 'tool':
                                this.addMessage({
                                    conversationId,
                                    type: 'tool',
                                    content: chunk.content,
                                    data: chunk,
                                    streamId,
                                });
                                break;
                            case 'pending_report':
                                break;
                        }
                    },
                    onEnd: (finalState) => {
                        const activeStreamId = this.activeStreamIds[conversationId];
                        const isActiveStream = activeStreamId === streamId;
                        if (!finalState || !isActiveStream) return;

                        this.$patch((state) => {
                            const previous = state.conversations[conversationId];
                            state.conversations[conversationId] = {
                                conversationId,
                                lensLocked: previous?.lensLocked,
                                lockedLens: previous?.lockedLens,
                                contextFollowsPage: previous?.contextFollowsPage,
                                ...finalState,
                                ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                            };
                            delete state.activeStreamIds[conversationId];
                        });

                        if (Array.isArray(finalState.pending_reports)) {
                            this.reconcilePendingReports({
                                conversationId,
                                entries: finalState.pending_reports,
                            });
                        }
                    },
                    onError: (error) => {
                        throw error;
                    },
                });

                await streamingProcessor.process(data);
            } catch (error) {
                if (previousEntry && error?.name !== 'AbortError') {
                    this.addPendingReport({ conversationId, entry: previousEntry });
                    this.startPollingReport({ conversationId, reportId });
                }
            } finally {
                if (this.abortControllers[conversationId] === abortController) {
                    this.stopLoading(conversationId);
                    this.clearAbortController({ conversationId });
                }
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useAskPendoStore, module.hot));
}
